import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import { resolveTenantContext } from "../tenantContext";

const TenantCtx = createContext(null);

async function resolveAndCommit(uid, firestoreData, setters) {
  const { setTenantContext, setActiveBandId, setActiveBandName, setUserRole, setBandOwnerId } = setters;
  const resolved = await resolveTenantContext(uid, firestoreData || {});
  setTenantContext(resolved);
  setActiveBandId(resolved.bandId);
  setActiveBandName(resolved.bandName);
  setUserRole(resolved.role);
  if (resolved.bandId) {
    try {
      const snap = await getDoc(doc(db, "bands", resolved.bandId));
      setBandOwnerId(snap.data()?.ownerId ?? null);
    } catch {
      setBandOwnerId(null);
    }
  } else {
    setBandOwnerId(null);
  }
  return resolved;
}

export function TenantProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenantContext, setTenantContext] = useState(null);
  const [isResolvingTenant, setIsResolvingTenant] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [activeBandId, setActiveBandId] = useState(null);
  const [activeBandName, setActiveBandName] = useState("");
  const [userRole, setUserRole] = useState("member");
  const [bandOwnerId, setBandOwnerId] = useState(null);
  const isHandlingLoginRef = useRef(false);
  const postJoinBandIdRef = useRef(null);

  const setters = { setTenantContext, setActiveBandId, setActiveBandName, setUserRole, setBandOwnerId };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (isHandlingLoginRef.current) return;
      // Sign-in events during a pending invite redemption are skipped — the
      // onAuthStateChanged fires immediately after createUserWithEmailAndPassword
      // before handleLoginComplete runs, creating a race where the app becomes
      // "ready" with no tenant band and triggers an infinite /login redirect loop.
      // Sign-out must always be processed so the app transitions to logged-out state.
      if (currentUser && sessionStorage.getItem("pendingInviteCode")) return;

      setIsResolvingTenant(true);

      if (!currentUser) {
        setUser(null);
        setTenantContext(null);
        setActiveBandId(null);
        setActiveBandName("");
        setUserRole("member");
        setBandOwnerId(null);
        setIsResolvingTenant(false);
        setAuthChecked(true);
        return;
      }

      setUser(currentUser);
      const pendingBandId = postJoinBandIdRef.current;
      postJoinBandIdRef.current = null;
      const seed = pendingBandId ? { lastActiveBandId: pendingBandId } : undefined;

      try {
        await resolveAndCommit(currentUser.uid, seed, setters);
      } catch (err) {
        console.error("Error loading user context:", err);
      } finally {
        setIsResolvingTenant(false);
        setAuthChecked(true);
      }
    });
    return () => unsubscribe();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Called at the start of handleLoginComplete to suppress the auth listener
  // and immediately reflect the signing-in user in the UI.
  const beginLogin = (currentUser) => {
    isHandlingLoginRef.current = true;
    setIsResolvingTenant(true);
    if (currentUser) setUser(currentUser);
  };

  // Called when handleLoginComplete has finished its invite/auth work.
  // Resolves the tenant, commits all state, and releases the suppress flag.
  // If firestoreData contains lastActiveBandId it is stashed so any auth
  // listener re-fire (Firestore replication lag after band join) seeds correctly.
  const finishLogin = async (currentUser, firestoreData = {}) => {
    try {
      if (firestoreData?.lastActiveBandId) {
        postJoinBandIdRef.current = firestoreData.lastActiveBandId;
      }
      const resolved = await resolveAndCommit(currentUser.uid, firestoreData, setters);
      setAuthChecked(true);
      setIsResolvingTenant(false);
      return resolved;
    } catch (err) {
      setIsResolvingTenant(false);
      setAuthChecked(true);
      throw err;
    } finally {
      isHandlingLoginRef.current = false;
    }
  };

  const patchTenant = (updates) => {
    if ("bandName" in updates) {
      setActiveBandName(updates.bandName);
      setTenantContext((prev) => (prev ? { ...prev, bandName: updates.bandName } : prev));
    }
    if ("ownerId" in updates) {
      setBandOwnerId(updates.ownerId);
    }
  };

  const isOwner = bandOwnerId != null && bandOwnerId === user?.uid;
  const isReady = authChecked && (!user || !isResolvingTenant);

  return (
    <TenantCtx.Provider value={{
      user,
      tenantContext,
      role: userRole,
      bandId: activeBandId,
      bandName: activeBandName,
      ownerId: bandOwnerId,
      isOwner,
      isResolvingTenant,
      authChecked,
      isReady,
      beginLogin,
      finishLogin,
      patchTenant,
    }}>
      {children}
    </TenantCtx.Provider>
  );
}

export function useTenant() {
  const ctx = useContext(TenantCtx);
  if (!ctx) throw new Error("useTenant must be used within TenantProvider");
  return ctx;
}
